// The rows of a run's timeline: a tool call (running or done, opens to show what it did), the model thinking, the
// session getting ready, the clean-up. Used by the chat and by the Runs page. Every row opens by click, Enter or
// Space; what is open stays open while the run goes on. A running row shows its latest activity under it, and says
// so when nothing has happened for a while (STALL_MS), so a long step never looks like a hang.
import { memo, useEffect, useState, type KeyboardEvent } from "react";
import { api } from "./api";
import { secs, useNow } from "./clock";
import { IconChevron, IconDone, IconFailed, IconRetried, IconRunning, IconThinking, IconWarning } from "./icons";
import { Markdown } from "./markdown";
import type { Line, Lines, Run, Stage, ToolItem } from "./timeline";

export const STALL_MS = 10_000;            // nothing new this long: the row says what it is still waiting on
export const STALL_WARN_MS = 30_000;       // ... and in amber after this long
export const THINK_DELAY_MS = 300;         // a thinking row appears only after this: quick turns do not flicker
export const MCP_WAIT_MS = 60_000;         // Claude Code silent this long: "waiting for its next call", not "thinking"
                                           // (the app cannot tell thinking from gone; the server ends the run at 2 min)

const ARG_KEYS = ["text", "keys", "summary", "description", "command", "answer", "title", "query"];
export function argOf(input?: Record<string, unknown>): string {
  if (!input) return "";
  for (const k of ARG_KEYS) if (typeof input[k] === "string" && input[k]) return `“${input[k] as string}”`;
  if (Array.isArray(input.steps)) {                  // key_sequence: Win · “Outlook” · Enter
    return (input.steps as Record<string, unknown>[]).map((st) =>
      typeof st.key === "string" ? st.key : typeof st.text === "string" ? `“${st.text}”` : "…").join(" · ");
  }
  if (typeof input.ref === "number" && input.ref) return `ref ${input.ref}`;
  if (Array.isArray(input.items)) return `${input.items.length} items`;
  if (typeof input.why === "string" && input.why) return input.why;   // the model's own reason, as a last resort
  return "";
}

export function verdictOf(s: ToolItem): "ok" | "fail" | "unclear" | "skipped" | "" {
  if (s.skipped) return "skipped";
  const v = (s.verdict || "").toLowerCase();
  if (v.startsWith("ok") || v === "pass" || v === "passed") return "ok";
  if (v.startsWith("fail") || v.startsWith("error")) return "fail";
  return v ? "unclear" : "";
}

function StatusIcon({ s }: { s: ToolItem }) {
  if (!s.done) return <span className="mark run"><IconRunning /></span>;
  const v = verdictOf(s);
  return v === "ok" ? <span className="mark ok"><IconDone /></span>
    : v === "fail" ? <span className="mark fail"><IconFailed /></span>
    : v === "skipped" ? <span className="mark unclear"><IconRetried /></span>
    : <span className="mark unclear"><IconWarning /></span>;
}

const toggleKeys = (fn: () => void) => (e: KeyboardEvent) => {
  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); }
};

/** The stall note for a running row: "still waiting for the screen to settle · 18 s", amber after STALL_WARN_MS. */
function Stall({ run, now }: { run: Run; now: number }) {
  const quiet = now - run.lastAt;
  if (quiet < STALL_MS) return null;
  return (
    <span className={`stall ${quiet >= STALL_WARN_MS ? "warn" : ""}`}>
      {quiet >= STALL_WARN_MS && <IconWarning />}still {run.lastLabel ?? "working"} · {secs(quiet)}
    </span>
  );
}

/** Activity lines: label, repeats (×3), time from the first line. Warnings in amber. */
export function ActivityLines({ act, empty }: { act: Lines; empty?: string }) {
  if (!act.lines.length) return empty ? <div className="act-empty">{empty}</div> : null;
  const t0 = act.lines[0].at;
  return (
    <ol className="act">
      {act.hidden > 0 && <li className="act-more">{act.hidden} earlier line{act.hidden > 1 ? "s" : ""}</li>}
      {act.lines.map((l: Line) => (
        <li key={l.seq} className={`act-line ${l.level !== "info" ? "warn" : ""}`}>
          <span className="act-icon">{l.level !== "info" ? <IconWarning /> : <i />}</span>
          <span className="act-label">{l.label}{l.count > 1 && <span className="act-count"> ×{l.count}</span>}</span>
          <span className="act-at">+{(l.at - t0).toFixed(1)} s</span>
        </li>
      ))}
    </ol>
  );
}

/** A step's whole activity, from the run's saved events (a finished step whose lines the page does not have). */
function useSavedActivity(run: string, query: { item?: number; stage?: string }, want: boolean) {
  const [got, setGot] = useState<{ act: Lines; gone: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!want || got || loading) return;
    setLoading(true);
    api.runActivity(run, query).then((r) => setGot({
      act: { lines: r.lines.map((l, i) => ({ seq: i, cls: l.class, kind: l.kind, label: l.label, level: l.level, at: l.at ?? 0, count: l.count })),
        hidden: 0 }, gone: r.gone }))
      .catch(() => setGot({ act: { lines: [], hidden: 0 }, gone: true })).finally(() => setLoading(false));
  }, [want]); // eslint-disable-line react-hooks/exhaustive-deps
  return { got, loading };
}

export const StepRow = memo(function StepRow({ s, run, live }: { s: ToolItem; run: Run; live: boolean }) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((o) => !o);
  const running = !s.done && live && !run.status;
  // a finished step whose lines this page does not have in full: read them from the run's events when opened
  const saved = useSavedActivity(run.id, { item: s.n }, open && s.done && (s.act.lines.length === 0 || s.act.hidden > 0));
  const latest = s.act.lines.at(-1);
  return (
    <div className={`row ${running ? "running" : ""}`}>
      <div className="step" role="button" tabIndex={0} aria-expanded={open} onClick={toggle} onKeyDown={toggleKeys(toggle)}>
        <IconChevron open={open} />
        <StatusIcon s={s} />
        <span className="tool">{s.tool || "…"}</span>
        <span className="arg">{s.skipped ? "not done: you took over" : argOf(s.input)}</span>
        {running ? <RunningTime since={s.startedAt ?? run.lastAt} /> : s.ms != null && <span className="ms">{secs(s.ms)}</span>}
      </div>
      {running && !open && latest && <RunningLine run={run} label={latest.label} warn={latest.level !== "info"} />}
      {open && (
        <div className="step-detail">
          {running && <><ActivityLines act={s.act} empty="Starting…" /><RunningLine run={run} /></>}
          {!running && (
            <>
              {saved.loading ? <div className="act-empty"><IconRunning /> Loading what it did…</div>
                : <ActivityLines act={saved.got && !saved.got.gone ? saved.got.act : s.act}
                    empty={saved.got?.gone ? "Its details were cleaned up." : undefined} />}
              {s.text && <div className="result"><Markdown text={s.text} /></div>}
              {s.shot ? <div className="shot"><img alt="The screen after this step" loading="lazy" src={`/api/runs/${run.id}/files/${s.shot}`} /></div>
                : s.image && <div className="shot"><img alt="The screen after this step" src={`data:image/jpeg;base64,${s.image}`} /></div>}
            </>
          )}
        </div>
      )}
    </div>
  );
});

function RunningTime({ since }: { since: number }) {
  const now = useNow();
  return <span className="ms live">{secs(Math.max(0, now - since))}</span>;
}

/** Under a running row: its latest activity, or how long it has been quiet. */
function RunningLine({ run, label, warn }: { run: Run; label?: string; warn?: boolean }) {
  const now = useNow();
  const stall = now - run.lastAt >= STALL_MS;
  if (!label && !stall) return null;
  return (
    <div className={`latest ${warn ? "warn" : ""}`}>
      {stall ? <Stall run={run} now={now} /> : label}
    </div>
  );
}

const BRAIN: Record<string, string> = { claude: "Claude", codex: "Codex", mcp: "Claude Code (MCP)", scripted: "The agent" };

/** The model thinking between tool calls (after THINK_DELAY_MS): what ran meanwhile opens under it. A person's own
 *  Claude Code silent for MCP_WAIT_MS is shown as waited for, without the animation: it may have gone. */
export function ThinkingRow({ run, brain }: { run: Run; brain: string }) {
  const now = useNow();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);       // its own timer: the shared clock ticks only once a second
  useEffect(() => {
    setShown(false);
    if (!run.think.on) return;
    const t = window.setTimeout(() => setShown(true), Math.max(0, THINK_DELAY_MS - (Date.now() - run.think.since)));
    return () => clearTimeout(t);
  }, [run.think.on, run.think.since]);
  const toggle = () => setOpen((o) => !o);
  if (!run.think.on || run.status || !shown) return null;
  const act = run.stages.thinking?.act;
  const who = run.think.brain ?? brain;
  const quiet = Math.max(now, Date.now()) - run.think.since;
  const waiting = who === "mcp" && quiet >= MCP_WAIT_MS;
  return (
    <div className={`row ${waiting ? "" : "running"}`}>
      <div className="step thinking" role="button" tabIndex={0} aria-expanded={open} onClick={toggle} onKeyDown={toggleKeys(toggle)}>
        <IconChevron open={open} />
        <span className={`mark think ${waiting ? "still" : ""}`}><IconThinking /></span>
        <span className="arg">{waiting ? "Waiting for Claude Code’s next call" : `${BRAIN[who] ?? "The agent"} is thinking`}</span>
        <span className="ms live">{waiting ? `last call ${secs(quiet)} ago` : secs(quiet)}</span>
      </div>
      {open && <div className="step-detail"><ActivityLines act={act ?? { lines: [], hidden: 0 }} empty="Nothing else is happening on the PC." /></div>}
      {!open && now - run.lastAt >= STALL_MS && run.lastAt > run.think.since && <RunningLine run={run} />}
    </div>
  );
}

const PART: Record<string, string> = { pointer: "pointer", curve: "pointer speed", background: "background", home: "home" };

/** The session getting ready (running: what it is doing; done: "Ready in 12 s", opens to its parts). */
export function StartupRow({ run, stage, readyS }: { run: Run; stage?: Stage; readyS?: number | null }) {
  const now = useNow();
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((o) => !o);
  const saved = useSavedActivity(run.id, { stage: "startup" }, open && !stage?.on && !(stage?.act.lines.length));
  if (!stage && readyS == null) return null;
  const on = !!stage?.on && !run.status;
  const latest = stage?.act.lines.at(-1);
  const measuring = stage?.act.lines.some((l) => l.label === "measuring how the pointer moves");
  const seconds = stage?.seconds ?? readyS ?? null;
  return (
    <div className={`row ${on ? "running" : ""}`}>
      <div className="step setup" role="button" tabIndex={0} aria-expanded={open} onClick={toggle} onKeyDown={toggleKeys(toggle)}>
        <IconChevron open={open} />
        {on ? <span className="mark run"><IconRunning /></span> : <span className="mark ok"><IconDone /></span>}
        <span className="arg">{on ? (measuring ? "Measuring how the pointer moves (about a minute, once per screen size)" : "Getting ready")
          : `Ready${seconds != null ? ` in ${secs(seconds * 1000)}` : ""}`}</span>
        {on && <span className="ms live">{secs(now - (stage?.since ?? now))}</span>}
      </div>
      {on && !open && latest && <RunningLine run={run} label={latest.label} />}
      {open && (
        <div className="step-detail">
          {stage?.parts && Object.keys(stage.parts).length > 0 && (
            <div className="parts">{Object.entries(stage.parts).map(([k, v]) => `${PART[k] ?? k} ${secs(v * 1000)}`).join(" · ")}</div>
          )}
          {saved.loading ? <div className="act-empty"><IconRunning /> Loading…</div>
            : <ActivityLines act={stage?.act.lines.length ? stage.act : saved.got?.act ?? { lines: [], hidden: 0 }}
                empty={saved.got?.gone ? "Its details were cleaned up." : on ? "Starting…" : undefined} />}
        </div>
      )}
    </div>
  );
}

/** After the agent finished: closing what it opened. Shown only while it runs. */
export function CleanupRow({ run }: { run: Run }) {
  const now = useNow();
  const st = run.stages.cleanup;
  if (!st?.on || run.status) return null;
  const latest = st.act.lines.at(-1);
  return (
    <div className="row running">
      <div className="step setup">
        <span className="chev-space" />
        <span className="mark run"><IconRunning /></span>
        <span className="arg">Cleaning up{latest ? ` · ${latest.label}` : ""}</span>
        <span className="ms live">{secs(now - st.since)}</span>
      </div>
    </div>
  );
}
